import Link from "next/link";
import type { ComponentPropsWithRef, ReactNode } from "react";

/**
 * THE SHEET'S PARALLELOGRAM CONTROL, as one component instead of ten hand-typed
 * copies of its markup (`.sheet-btn` in app/globals.css).
 *
 * It exists because the primitive's shape is not expressible in CSS alone. The
 * wrapper is skewed -12deg and `.sheet-btn > * > *` counter-skews the
 * GRANDCHILD back upright, so every label needs an inner element to carry that
 * counter-skew. A bare text node has nothing to put it on and ships sheared —
 * which is exactly what `AddToCalendarButton` did for a build, being the one
 * call site that wrote `<span className="sheet-btn__label">{label}</span>`
 * while the other five wrote the nested pair. A structural requirement that
 * nine call sites remember and the tenth forgets is a component, not a
 * convention.
 *
 * The cap's glyph is DECORATIVE and `aria-hidden`, so the accessible name of
 * every one of these is exactly `label`. `→` for going somewhere, `+` for
 * adding a thing, `×` for removing one — the same vocabulary the world already
 * shipped, and all three are ordinary Saira glyphs.
 *
 * ANYTHING OUTSIDE THAT IS A DRAWN ICON, NOT A UNICODE SYMBOL. `↻` was the
 * obvious cap for "Sync now" and Saira does not carry it: it silently falls
 * back to the platform's own face, which is a second typeface inside a control
 * whose whole job is to be the same shape everywhere. `document.fonts.check`
 * says "true" for it — that API answers about the family, not the glyph — so
 * the way to tell is to measure, and it came back 12.78px against a 12.43px
 * .notdef probe. `SyncIcon` below is the drawn answer, in the 1.6 stroke the
 * app's other icons use; the login control's Google mark is the precedent for
 * an SVG cap.
 *
 * Server-safe: no hooks, no "use client". It renders inside client components
 * (AddToCalendarButton, DeleteAppointment) and server ones (the dashboard, the
 * visit page, login) alike.
 *
 * `ComponentPropsWithRef`, not `...WithoutRef`: on React 19 `ref` is an
 * ordinary prop on a function component, so it rides `...rest` to the element
 * with no `forwardRef` anywhere. That matters — `DeleteAppointment` focuses its
 * trigger after cancelling the confirm step, and without a ref it would have
 * been the one call site left hand-writing the markup, which is the exact hole
 * this component exists to close.
 */

/** The re-run cap: a circular arrow, drawn rather than borrowed from Unicode.
 * 14px against the cap's 11px type so it reads at the same optical weight as
 * the `→`/`+`/`×` characters beside it. */
export function SyncIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 12a8 8 0 11-2.34-5.66M20 4v4h-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The two spans, shared by both elements below. */
function SheetControlContent({ label, cap }: { label: ReactNode; cap?: ReactNode }) {
  return (
    <>
      <span className="sheet-btn__label">
        <span>{label}</span>
      </span>
      {cap !== undefined && (
        <span className="sheet-btn__cap" aria-hidden="true">
          <span>{cap}</span>
        </span>
      )}
    </>
  );
}

/** `--ghost` is the outlined variant: transparent label, `--ink` edge. Solid is
 * the filled one. Solid is the page's primary action and there is at most one;
 * everything else is a ghost. */
function classes(ghost: boolean, className?: string) {
  return ["sheet-btn", ghost ? "sheet-btn--ghost" : null, className].filter(Boolean).join(" ");
}

type SharedProps = {
  label: ReactNode;
  /** Omit for no cap at all; the world's controls all carry one. */
  cap?: ReactNode;
  ghost?: boolean;
};

export function SheetButton({
  label,
  cap = "→",
  ghost = false,
  className,
  type = "button",
  ...rest
}: SharedProps & Omit<ComponentPropsWithRef<"button">, "children" | "className"> & { className?: string }) {
  return (
    <button {...rest} type={type} className={classes(ghost, className)}>
      <SheetControlContent label={label} cap={cap} />
    </button>
  );
}

export function SheetLink({
  label,
  cap = "→",
  ghost = false,
  className,
  ...rest
}: SharedProps & Omit<ComponentPropsWithRef<typeof Link>, "children" | "className"> & { className?: string }) {
  return (
    <Link {...rest} className={classes(ghost, className)}>
      <SheetControlContent label={label} cap={cap} />
    </Link>
  );
}
